import PropTypes from "prop-types";
import * as React from "react";
import {
  IconButton,
  PauseIcon,
  PlayIcon,
  Slider,
  Typography,
} from "../../themeV2";
import { formatTime } from "../../util/videoRecorderUtils";
import s from "./PlaybackControls.module.css";

// Drives a <video> the caller owns rather than rendering one, so it sits over
// the element the recorder already uses for its preview.
function PlaybackControls({ videoRef }) {
  const [playing, setPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onTimeUpdate = () => setCurrentTime(video.currentTime);

    // Chrome reports Infinity for a MediaRecorder blob until it has seeked the
    // whole file; useVideoRecorder's applyChromeDurationFix is what resolves it.
    const readDuration = () => {
      if (video.duration && video.duration !== Infinity) {
        setDuration(video.duration);
      }
    };

    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onPause);
    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("loadedmetadata", readDuration);
    video.addEventListener("durationchange", readDuration);

    readDuration();
    setCurrentTime(video.currentTime);
    setPlaying(!video.paused);

    return () => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onPause);
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("loadedmetadata", readDuration);
      video.removeEventListener("durationchange", readDuration);
    };
  }, [videoRef]);

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused || video.ended) {
      video.play();
    } else {
      video.pause();
    }
  };

  const seek = (value) => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = value;
    }
  };

  return (
    <div className={s.root}>
      <IconButton
        variant="text"
        size="sm"
        label={playing ? "Pause" : "Play"}
        onClick={togglePlayback}
      >
        {playing ? <PauseIcon /> : <PlayIcon />}
      </IconButton>

      <Slider
        className={s.slider}
        min={0}
        max={duration || 1}
        step={0.1}
        value={currentTime}
        onValueChange={seek}
        aria-label="Playback position"
      />

      <Typography variant="label-md" color="variant">
        {`${formatTime(currentTime)} / ${formatTime(duration)}`}
      </Typography>
    </div>
  );
}

PlaybackControls.propTypes = {
  videoRef: PropTypes.object.isRequired,
};

export default PlaybackControls;
